import { Home, Building2, CreditCard, Receipt, Users, Tag, HelpCircle, ExternalLink, User as UserIcon, Database as DatabaseIcon } from 'lucide-react';
import { Link, useLocation } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import { useBrand } from '../brand/BrandProvider';
import { openPos } from '../api/pos';
import { useActiveBusiness, usePosAccess } from '../api/hooks';

/**
 * Business-owner sidebar. Every item links to a real page with mock data —
 * no "coming soon" placeholders. The SaaS web app's job is signup, billing,
 * subscription, multi-business management, and team — POS operations live
 * inside the Laravel POS at the business's own subdomain.
 */
export function Sidebar({ className, onNavigate }: { className?: string; onNavigate?: () => void }) {
  const brand = useBrand();
  const { data: activeBusiness } = useActiveBusiness();
  // Trial-ended / suspended businesses lose the POS door (SubscriptionGateBanner explains why).
  const { blocked: posBlocked } = usePosAccess(activeBusiness?.id);
  return (
    <nav
      className={cn(
        'w-60 h-full bg-zinc-50 border-r border-zinc-200',
        'dark:bg-zinc-900 dark:border-zinc-700',
        'flex flex-col',
        className,
      )}
      aria-label="Primary"
    >
      <div className="flex-1 overflow-y-auto nice-scroll px-2.5 py-3 space-y-0.5">
        <NavItem to="/" icon={<Home className="w-4 h-4" />} label="Home" onNavigate={onNavigate} />
        <NavItem to="/businesses" icon={<Building2 className="w-4 h-4" />} label="My businesses" onNavigate={onNavigate} />

        <SectionLabel className="mt-6">Billing</SectionLabel>
        <NavItem to="/subscription" icon={<CreditCard className="w-4 h-4" />} label="Subscription" onNavigate={onNavigate} />
        <NavItem to="/billing" icon={<Receipt className="w-4 h-4" />} label="Invoices" onNavigate={onNavigate} />
        <NavItem to="/add-ons" icon={<Tag className="w-4 h-4" />} label="Add-ons" onNavigate={onNavigate} />

        <SectionLabel className="mt-6">Account</SectionLabel>
        <NavItem to="/team" icon={<Users className="w-4 h-4" />} label="Team" onNavigate={onNavigate} />
        <NavItem to="/database" icon={<DatabaseIcon className="w-4 h-4" />} label="Database" onNavigate={onNavigate} />
        <NavItem to="/profile" icon={<UserIcon className="w-4 h-4" />} label="Profile" onNavigate={onNavigate} />
      </div>

      <div className="border-t border-zinc-200 dark:border-zinc-700 p-2.5 space-y-0.5">
        {!posBlocked && (
          <button
            type="button"
            onClick={() => openPos(activeBusiness?.id, '/home')}
            className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-sm transition text-zinc-700 dark:text-zinc-200 hover:bg-white dark:hover:bg-zinc-800/70 hover:text-zinc-950 dark:hover:text-zinc-50"
          >
            <span className="text-zinc-500 dark:text-zinc-400 flex-shrink-0">
              <ExternalLink className="w-4 h-4" />
            </span>
            <span className="flex-1 truncate text-left">Open POS</span>
          </button>
        )}
        <a
          href={`mailto:${brand.supportEmail}`}
          className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-sm transition text-zinc-500 dark:text-zinc-400 hover:bg-white dark:hover:bg-zinc-800/70 hover:text-zinc-950 dark:hover:text-zinc-50"
        >
          <span className="text-zinc-500 dark:text-zinc-400 flex-shrink-0">
            <HelpCircle className="w-4 h-4" />
          </span>
          <span className="flex-1 truncate">Help & support</span>
        </a>
      </div>
    </nav>
  );
}

function SectionLabel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'px-2.5 mb-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400',
        className,
      )}
    >
      {children}
    </div>
  );
}

function NavItem({
  to,
  icon,
  label,
  onNavigate,
}: {
  to: string;
  icon: ReactNode;
  label: string;
  onNavigate?: () => void;
}) {
  const location = useLocation();
  const active = location.pathname === to;

  return (
    <Link
      to={to}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-sm transition',
        active
          ? 'bg-white dark:bg-zinc-800 text-zinc-950 dark:text-zinc-50 shadow-xs font-medium'
          : 'text-zinc-700 dark:text-zinc-200 hover:bg-white dark:hover:bg-zinc-800/70 hover:text-zinc-950 dark:hover:text-zinc-50',
      )}
    >
      <span
        className={cn(
          'flex-shrink-0',
          active ? 'text-brand dark:text-brand-dm' : 'text-zinc-500 dark:text-zinc-400',
        )}
      >
        {icon}
      </span>
      <span className="flex-1 min-w-0 truncate">{label}</span>
    </Link>
  );
}
